import { ConsignmentParcelItemApiModel, MarketplaceOrderApiModel, ProductApiModel } from '~/lib/api/smartsend/types';

/**
 * Presents a consignment content row as a Product so the same product validation can run over both
 * shapes.
 *
 * `Meta` is resolved from the marketplace order line the row came from, not from the row itself.
 * Contents deliberately carry no Meta - the API confirms Meta belongs to the line, since
 * `OrderMapper.GetVtn` reads the Royal Mail virtual tracking number from `Product.Meta['vtn']` on the
 * line and nothing marketplace-shaped is ever read from contents. Lines are the linkage layer,
 * contents the declaration layer.
 *
 * This used to hardcode `Meta: {}`, which made any product rule keyed on a Meta field unsatisfiable
 * for a multi-parcel item no matter what the user entered. Passing `lines` fixes that without
 * fabricating Meta onto contents. `Manual` rows have no line to resolve against and legitimately get
 * an empty Meta.
 */
export const MapConsignmentContentToProduct = (
  o: ConsignmentParcelItemApiModel,
  lines?: MarketplaceOrderApiModel[]
): ProductApiModel => {
  return {
    Description: o.Description,
    Sku: o.Sku,
    TariffCode: o.TariffCode,
    // TODO (follow-up PR): an absent CountryOfManufacture becomes "GBR" once this reaches
    // OrderMapper.MapParcels, but stays blank on the single-parcel path. See the fuller note at the
    // consignment ProductValidation branch in SmartSendValidation.ts.
    CountryOfManufacture: o.CountryOfManufacture,
    Id: o.Id,
    Meta: lines?.find((l) => l.Id === o.Id)?.Product?.Meta ?? {},
    Value: o.Value
  };
};

export const MapMarketplaceOrderlineToConsignmentParcel = (
  o: MarketplaceOrderApiModel
): {
  Description: string;
  Sku: string;
  TariffCode: string;
  CountryOfManufacture: string;
  Quantity: number;
} => {
  return {
    Description: o.Product.Description,
    Sku: o.Product.Sku,
    TariffCode: o.Product.TariffCode,
    CountryOfManufacture: o.Product.CountryOfManufacture,
    Quantity: o.Quantity
  };
};
